import React, { useState, useEffect, useMemo } from "react";
import { Bar, Pie, Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from "chart.js";
import { format, subDays, parseISO } from "date-fns";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

const Reports = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [filterRange, setFilterRange] = useState("last30days");

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch("/api/inquiries");
      if (!response.ok) throw new Error("Failed to fetch inquiries");
      const data = await response.json();
      const normalizedData = data.map((inq) => ({
        ...inq,
        id: inq.id.toString(),
        createdAt: parseISO(inq.createdAt),
      }));
      setInquiries(normalizedData);
    } catch (err) {
      setError(err.message || "Error loading inquiries");
    } finally {
      setLoading(false);
    }
  };

  // Compute filtered inquiries based on date range
  const filteredInquiries = useMemo(() => {
    let startDate, endDate;
    const today = new Date();

    switch (filterRange) {
      case "last7days":
        startDate = subDays(today, 7);
        endDate = today;
        break;
      case "last30days":
        startDate = subDays(today, 30);
        endDate = today;
        break;
      case "custom":
        startDate = dateRange.start ? new Date(dateRange.start) : subDays(today, 30);
        endDate = dateRange.end ? new Date(dateRange.end) : today;
        break;
      default:
        startDate = subDays(today, 30);
        endDate = today;
    }

    return inquiries.filter((inq) => {
      const createdAt = new Date(inq.createdAt);
      return createdAt >= startDate && createdAt <= endDate;
    });
  }, [inquiries, filterRange, dateRange]);

  // Inquiry Volume Over Time (Bar Chart)
  const volumeData = useMemo(() => {
    const dates = [];
    const volumes = [];
    const dayMap = {};

    filteredInquiries.forEach((inq) => {
      const date = format(new Date(inq.createdAt), "yyyy-MM-dd");
      dayMap[date] = (dayMap[date] || 0) + 1;
    });

    const startDate = filterRange === "last7days" ? subDays(new Date(), 7) : subDays(new Date(), 30);
    for (let d = startDate; d <= new Date(); d = new Date(d.setDate(d.getDate() + 1))) {
      const dateStr = format(d, "yyyy-MM-dd");
      dates.push(dateStr);
      volumes.push(dayMap[dateStr] || 0);
    }

    return {
      labels: dates,
      datasets: [
        {
          label: "Inquiries per Day",
          data: volumes,
          backgroundColor: "#2563eb",
        },
      ],
    };
  }, [filteredInquiries, filterRange]);

  // Inquiry Type Segmentation (Pie Chart)
  const typeData = useMemo(() => {
    const typeMap = {};
    filteredInquiries.forEach((inq) => {
      const type = inq.course || "Other";
      typeMap[type] = (typeMap[type] || 0) + 1;
    });

    return {
      labels: Object.keys(typeMap),
      datasets: [
        {
          label: "Inquiry Types",
          data: Object.values(typeMap),
          backgroundColor: [
            "#f87171",
            "#34d399",
            "#fbbf24",
            "#9ca3af",
            "#a78bfa",
            "#f472b6",
            "#60a5fa",
          ],
        },
      ],
    };
  }, [filteredInquiries]);

  // Simulated Response Time (Line Chart)
  const responseTimeData = useMemo(() => {
    const dates = [];
    const avgTimes = [];
    const dayMap = {};

    filteredInquiries.forEach((inq) => {
      const date = format(new Date(inq.createdAt), "yyyy-MM-dd");
      // Simulate response time (in hours) since backend doesn't provide this
      const simulatedResponseTime = Math.floor(Math.random() * 48); // Random 0-48 hours
      if (!dayMap[date]) dayMap[date] = { total: 0, count: 0 };
      dayMap[date].total += simulatedResponseTime;
      dayMap[date].count += 1;
    });

    const startDate = filterRange === "last7days" ? subDays(new Date(), 7) : subDays(new Date(), 30);
    for (let d = startDate; d <= new Date(); d = new Date(d.setDate(d.getDate() + 1))) {
      const dateStr = format(d, "yyyy-MM-dd");
      dates.push(dateStr);
      const avgTime = dayMap[dateStr] ? dayMap[dateStr].total / dayMap[dateStr].count : 0;
      avgTimes.push(avgTime);
    }

    return {
      labels: dates,
      datasets: [
        {
          label: "Average Response Time (hours)",
          data: avgTimes,
          borderColor: "#34d399",
          fill: false,
        },
      ],
    };
  }, [filteredInquiries, filterRange]);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["InquiryID,Date,Course,FullName,Email,Mobile,Message"];
    const rows = filteredInquiries.map((inq) => [
      inq.id,
      format(inq.createdAt, "yyyy-MM-dd HH:mm:ss"),
      inq.course,
      inq.fullName,
      inq.email,
      inq.mobileNumber,
      `"${inq.message.replace(/"/g, '""')}"`,
    ].join(","));
    const csv = [...headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inquiry_reports.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-blue-600">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-blue-900 mb-6 text-center">
          Inquiry Reports
        </h1>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 mb-2">Date Range</label>
              <select
                value={filterRange}
                onChange={(e) => setFilterRange(e.target.value)}
                className="w-full p-2 border rounded-lg"
              >
                <option value="last7days">Last 7 Days</option>
                <option value="last30days">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            {filterRange === "custom" && (
              <>
                <div>
                  <label className="block text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange((prev) => ({ ...prev, start: e.target.value }))
                    }
                    className="w-full p-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange((prev) => ({ ...prev, end: e.target.value }))
                    }
                    className="w-full p-2 border rounded-lg"
                  />
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Export to CSV
            </button>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Inquiry Volume */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Inquiry Volume Over Time</h2>
            <div style={{ height: "300px" }}>
              <Bar
                data={volumeData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { title: { display: true, text: "Date" } },
                    y: { title: { display: true, text: "Number of Inquiries" }, beginAtZero: true },
                  },
                }}
              />
            </div>
          </div>

          {/* Inquiry Type Segmentation */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Inquiry Type Segmentation</h2>
            <div style={{ height: "300px" }}>
              <Pie
                data={typeData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: "top" },
                    title: { display: true, text: "Inquiries by Course" },
                  },
                }}
              />
            </div>
          </div>

          {/* Response Time */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Average Response Time</h2>
            <div style={{ height: "300px" }}>
              <Line
                data={responseTimeData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: { title: { display: true, text: "Date" } },
                    y: { title: { display: true, text: "Hours" }, beginAtZero: true },
                  },
                }}
              />
            </div>
          </div>

          {/* Summary Table */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Summary Statistics</h2>
            <table className="w-full">
              <tbody>
                <tr className="border-b">
                  <td className="py-2 px-4 font-medium">Total Inquiries</td>
                  <td className="py-2 px-4">{filteredInquiries.length}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-4 font-medium">Most Popular Course</td>
                  <td className="py-2 px-4">
                    {typeData.labels[typeData.datasets[0].data.indexOf(Math.max(...typeData.datasets[0].data))] || "N/A"}
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-2 px-4 font-medium">Average Response Time</td>
                  <td className="py-2 px-4">
                    {responseTimeData.datasets[0].data.length
                      ? (responseTimeData.datasets[0].data.reduce((a, b) => a + b, 0) / responseTimeData.datasets[0].data.length).toFixed(1) + " hours"
                      : "N/A"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
